import type { LucideIcon } from "lucide-react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CollapsibleCardProps {
  icon: LucideIcon;
  title: string;
  testId: string;
  showExperimentalBadge?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function CollapsibleCard({
  icon: Icon,
  title,
  testId,
  showExperimentalBadge = false,
  className,
  children,
}: CollapsibleCardProps) {
  const { t } = useTranslation("jobDetails");
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div
      className={`bg-white rounded-[1.5rem] shadow-base p-6 overflow-hidden ${className ?? ""}`}
      data-testid={`${testId}-card`}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full h-7"
        data-testid={`${testId}-header`}
      >
        <div className="flex items-center gap-2">
          <Icon
            className="w-[1.125rem] h-[1.125rem] text-foreground"
            data-testid={`${testId}-icon`}
          />
          <h2 className="text-lg font-semibold text-foreground leading-7">
            {title}
          </h2>
          {showExperimentalBadge && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-fuchsia-600 bg-white border border-fuchsia-600 rounded-lg cursor-default"
                    data-testid="experimental-badge"
                  >
                    {t("bookDetails.experimental")}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  className="max-w-xs"
                  data-testid="experimental-tooltip"
                >
                  <p>{t("bookDetails.experimentalTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown
            className="w-[1.125rem] h-[1.125rem] text-foreground"
            data-testid="chevron-down"
          />
        ) : (
          <ChevronRight
            className="w-[1.125rem] h-[1.125rem] text-foreground"
            data-testid="chevron-right"
          />
        )}
      </button>

      {/* Content with smooth animation */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid={`${testId}-content`}
      >
        {children}
      </div>
    </div>
  );
}
